import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { PublicDeveloperDetailsParams } from "../../../models";
import { MapProjectCoordinates } from "../../../models/shared/mapProjectCoordinates";
import { searchProjects } from "../../../service/publicquery";
import { Status } from "../../../service/Shared";
import { useRouterContext } from "../../../useRouterContext";
import { useScrollToTop } from "../../../useScrollToTop";
import { getProjectPathnameForBackNavigation } from "../../../utils/routes";
import { DetailsMapConstants } from "../constants";

interface UsePublicDeveloperDetailsPageTemplateReturnData {
  dataIsLoading: boolean;
  projectsCoordinates: MapProjectCoordinates[] | undefined;
  developerUuid: string | undefined;
  navigateBackToProject: () => void;
  showContactUsModal: boolean;
  setShowContactUsModal: Dispatch<SetStateAction<boolean>>;
}

export const usePublicDeveloperDetailsPageTemplate = (): UsePublicDeveloperDetailsPageTemplateReturnData => {
  useScrollToTop();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { previousLocation } = useRouterContext();
  const { developerUuid } = useParams<PublicDeveloperDetailsParams>();

  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [projectsCoordinates, setProjectsCoordinates] = useState<MapProjectCoordinates[] | undefined>();

  const fetchData = useCallback(async (): Promise<void> => {
    if (developerUuid) {
      const projectsRes = await searchProjects({
        paging: {
          limit: DetailsMapConstants.MAXIMUM_MARKERS,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: {
          results: {
            developer: {
              uuid: { operator: "eq", value: developerUuid },
            },
          },
        },
      });

      if (projectsRes.status === Status.Success && projectsRes.data) {
        setProjectsCoordinates(
          projectsRes.data.results
            .filter((project) => project.locationCoordinates !== null)
            .flatMap((project) => ({ coordinate: project.locationCoordinates as string }))
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchData().then(() => setDataIsLoading(false));
  }, [fetchData]);

  const navigateBackToProject = (): void => {
    const projectUuid = searchParams.get("projectUuid");
    if (projectUuid) {
      navigate(getProjectPathnameForBackNavigation(projectUuid, previousLocation));
    }
  };

  const [showContactUsModal, setShowContactUsModal] = useState(false);

  return {
    dataIsLoading,
    projectsCoordinates,
    developerUuid,
    navigateBackToProject,
    showContactUsModal,
    setShowContactUsModal,
  };
};
